import React, { useEffect, useState } from 'react';
import { Container, Card, Form, Button, Spinner, Alert, Badge, ListGroup, Accordion } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { apiClient } from '@/services/apiClient';
import './ReviewDetail.scss';

interface ReviewTaskCommentDto {
  guid: string;
  commentText: string;
  createdByEmail: string;
  createdDate: string;
}

interface ReviewTaskDto {
  guid: string;
  taskName: string;
  taskDescription: string;
  taskConfig?: string;
  sortOrder: number;
  isRequired: boolean;
  isComplete: boolean;
  completedDate?: string;
  commentCount: number;
  comments: ReviewTaskCommentDto[];
}

interface ReviewDetailDto {
  guid: string;
  reviewName: string;
  templateName: string;
  templateCategory: string;
  templateDescription: string;
  reviewTemplateConfig?: string;
  reviewStatus: string;
  assignedTo?: string;
  assignedDate: string;
  reviewDetails?: string;
  completedDate?: string;
  isComplete: boolean;
  tasks: ReviewTaskDto[];
  linkedReports?: ReviewLinkedReportDto[];
}

interface DecisionTaskConfig {
  options: string[];
  noteRequired: boolean;
  noteEnabled: boolean;
}

interface ReviewLinkedReportDto {
  m365ReportId: number;
  m365ReportRunGuid: string;
  reportTitle: string;
  reportLink?: string;
  reportFilename?: string;
  queuedDate?: string;
  sourceReviewGuid: string;
  sourceReviewName: string;
  isFromParentReview: boolean;
}

interface ReportCatalogEntry {
  key: string;
  m365ReportId: number;
  title: string;
  defaultOptions?: unknown;
  defaultProperties?: unknown;
}

interface ReportTriggerTaskConfig {
  reports: Array<string | { key?: string; m365ReportId?: number; title?: string; reportOptions?: unknown; reportProperties?: unknown }>;
  allowRegenerate: boolean;
  includeParentReports: boolean;
}

interface QueueReviewReportItem {
  m365ReportId: number;
  reportTitle: string;
  reportOptionsJson?: string;
  reportPropertiesJson?: string;
}

const ReviewDetail: React.FC = () => {
  const { reviewGuid } = useParams<{ reviewGuid: string }>();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const currentUserEmail = (accounts[0]?.username ?? '').toLowerCase();
  const [review, setReview] = useState<ReviewDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [decisionOutcome, setDecisionOutcome] = useState('');
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reportSubmittingTaskGuid, setReportSubmittingTaskGuid] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  useEffect(() => {
    loadReview();
  }, [reviewGuid]);

  // Auto-refresh when user returns to page
  useEffect(() => {
    const handleFocus = () => {
      if (reviewGuid) loadReview();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [reviewGuid]);

  const loadReview = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<ReviewDetailDto>(`/api/reviews/${reviewGuid}`);
      setReview(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load review');
    } finally {
      setLoading(false);
    }
  };

  const parseDecisionTaskConfig = (taskConfig?: string): DecisionTaskConfig | null => {
    if (!taskConfig) return null;

    try {
      const parsed = JSON.parse(taskConfig);
      const optionSource = parsed?.decisionOptions ?? parsed?.options ?? parsed?.decisions;
      if (!Array.isArray(optionSource)) {
        return null;
      }

      const options = optionSource
        .map((o: any) => {
          if (typeof o === 'string') return o.trim();
          if (o && typeof o === 'object') return (o.value || o.label || '').toString().trim();
          return '';
        })
        .filter((o: string) => !!o);

      if (options.length === 0) {
        return null;
      }

      const noteRequired = parsed?.decisionNoteRequired === true || parsed?.noteRequired === true || parsed?.requireNote === true;
      const noteEnabled = noteRequired || parsed?.decisionNoteEnabled === true || parsed?.noteEnabled === true || parsed?.allowNote === true;

      return { options, noteRequired, noteEnabled };
    } catch {
      return null;
    }
  };

  const parseReportCatalog = (templateConfig?: string): Record<string, ReportCatalogEntry> => {
    if (!templateConfig) return {};

    try {
      const parsed = JSON.parse(templateConfig);
      const catalog = parsed?.reportCatalog;
      if (!catalog || typeof catalog !== 'object' || Array.isArray(catalog)) {
        return {};
      }

      const result: Record<string, ReportCatalogEntry> = {};
      Object.entries(catalog as Record<string, any>).forEach(([key, value]) => {
        if (!value || typeof value !== 'object') return;
        const m365ReportId = Number(value.m365ReportId);
        if (!Number.isFinite(m365ReportId) || m365ReportId <= 0) return;

        result[key] = {
          key,
          m365ReportId,
          title: typeof value.title === 'string' && value.title.trim()
            ? value.title.trim()
            : `Report ${m365ReportId}`,
          defaultOptions: value.defaultOptions,
          defaultProperties: value.defaultProperties
        };
      });

      return result;
    } catch {
      return {};
    }
  };

  const parseReportTriggerTaskConfig = (taskConfig?: string): ReportTriggerTaskConfig | null => {
    if (!taskConfig) return null;

    try {
      const parsed = JSON.parse(taskConfig);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
      }

      const typeValue = typeof parsed.type === 'string' ? parsed.type.trim().toLowerCase() : '';
      const reportsValue = Array.isArray(parsed.reports) ? parsed.reports : [];
      const isReportTrigger = typeValue === 'reporttrigger' || typeValue === 'report-trigger' || typeValue === 'report_trigger';

      if (!isReportTrigger && reportsValue.length === 0) {
        return null;
      }

      return {
        reports: reportsValue,
        allowRegenerate: parsed.allowRegenerate === true,
        includeParentReports: parsed.includeParentReports !== false
      };
    } catch {
      return null;
    }
  };

  const resolveQueueItems = (
    reportTaskConfig: ReportTriggerTaskConfig,
    reportCatalog: Record<string, ReportCatalogEntry>
  ): QueueReviewReportItem[] => {
    const items: QueueReviewReportItem[] = [];

    reportTaskConfig.reports.forEach((entry) => {
      if (typeof entry === 'string') {
        const key = entry.trim();
        if (!key) return;

        const fromCatalog = reportCatalog[key];
        if (!fromCatalog) return;

        items.push({
          m365ReportId: fromCatalog.m365ReportId,
          reportTitle: fromCatalog.title,
          reportOptionsJson: fromCatalog.defaultOptions ? JSON.stringify(fromCatalog.defaultOptions) : undefined,
          reportPropertiesJson: fromCatalog.defaultProperties ? JSON.stringify(fromCatalog.defaultProperties) : undefined
        });
        return;
      }

      if (!entry || typeof entry !== 'object') return;
      const key = typeof entry.key === 'string' ? entry.key.trim() : '';
      const catalogEntry = key ? reportCatalog[key] : undefined;
      const m365ReportId = Number(entry.m365ReportId ?? catalogEntry?.m365ReportId);
      if (!Number.isFinite(m365ReportId) || m365ReportId <= 0) return;

      const reportTitle = typeof entry.title === 'string' && entry.title.trim()
        ? entry.title.trim()
        : (catalogEntry?.title || `Report ${m365ReportId}`);

      const reportOptions = entry.reportOptions ?? catalogEntry?.defaultOptions;
      const reportProperties = entry.reportProperties ?? catalogEntry?.defaultProperties;

      items.push({
        m365ReportId,
        reportTitle,
        reportOptionsJson: reportOptions ? JSON.stringify(reportOptions) : undefined,
        reportPropertiesJson: reportProperties ? JSON.stringify(reportProperties) : undefined
      });
    });

    const deduped = new Map<string, QueueReviewReportItem>();
    items.forEach((item) => {
      const key = `${item.m365ReportId}|${item.reportTitle}`;
      if (!deduped.has(key)) {
        deduped.set(key, item);
      }
    });

    return Array.from(deduped.values());
  };

  const handleQueueReports = async (taskGuid: string, config: ReportTriggerTaskConfig) => {
    if (!review) return;

    const reportCatalog = parseReportCatalog(review.reviewTemplateConfig);
    const queueItems = resolveQueueItems(config, reportCatalog);

    if (queueItems.length === 0) {
      setError('No report definitions were found for this step. Add reports in step config and/or template report catalog.');
      return;
    }

    try {
      setReportSubmittingTaskGuid(taskGuid);
      setError(null);
      setInfoMessage(null);

      await apiClient.post(`/api/reviews/${review.guid}/reports/queue`, {
        reports: queueItems
      });

      setInfoMessage(`Queued ${queueItems.length} report(s). Refreshing linked report status...`);
      await loadReview();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to queue reports for this review step.');
    } finally {
      setReportSubmittingTaskGuid(null);
    }
  };

  const handleAddComment = async (taskGuid: string, markComplete: boolean, config?: DecisionTaskConfig | null) => {
    const isDecisionTask = !!config;
    const trimmedComment = commentText.trim();

    if (isDecisionTask) {
      if (!decisionOutcome.trim()) {
        setError('Please select a decision');
        return;
      }

      if (config.noteRequired && !trimmedComment) {
        setError('A note is required for this decision');
        return;
      }
    } else if (!trimmedComment) {
      setError('Please enter a comment');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const sessionGuid = localStorage.getItem('sessionGuid');
      await apiClient.post(`/api/reviews/tasks/${taskGuid}/comments`, {
        commentText: trimmedComment,
        decisionOutcome: isDecisionTask ? decisionOutcome.trim() : null,
        isTaskComplete: isDecisionTask ? true : markComplete,
        sessionGuid: sessionGuid
      });

      setCommentText('');
      setDecisionOutcome('');
      setSelectedTask(null);
      // Reload to show updated comments and completion status
      await loadReview();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading review...</p>
      </Container>
    );
  }

  if (error || !review) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error || 'Review not found'}</Alert>
        <Button variant="warning" onClick={() => navigate('/reviews')}>
          Back to Reviews
        </Button>
      </Container>
    );
  }

  const completedTasks = review.tasks.filter(t => t.isComplete).length;
  const progressPercent = review.tasks.length > 0 
    ? Math.round((completedTasks / review.tasks.length) * 100) 
    : 0;
  const isCancelled = (review.reviewStatus || '').toLowerCase() === 'cancelled';
  const isSubmittedForFinalReview = (review.reviewStatus || '').toLowerCase() === 'submitted for final review';
  // The review is read-only if it's fully complete, or if it's submitted for final review
  // and the current user is NOT the current assignee (i.e. they are the original reviewer).
  const isFinalReviewer = isSubmittedForFinalReview &&
    currentUserEmail !== '' &&
    (review.assignedTo ?? '').toLowerCase() === currentUserEmail;
  const isReadOnly = review.isComplete || (isSubmittedForFinalReview && !isFinalReviewer);

  // The current step is the first incomplete required step.
  // Required steps must be completed in order — steps after the current required step are locked.
  // Non-required steps are always interactable regardless of position.
  const currentRequiredStepIndex = review.tasks.findIndex(t => !t.isComplete && t.isRequired);

  const isTaskInteractable = (task: ReviewTaskDto, index: number): boolean => {
    if (isReadOnly || task.isComplete) return false;
    if (!task.isRequired) return true; // optional steps can always be done
    if (currentRequiredStepIndex === -1) return true; // all required done
    return index === currentRequiredStepIndex;
  };

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2>{review.reviewName}</h2>
          <p className="text-muted mb-1">
            Template: {review.templateName} • Category: {review.templateCategory}
          </p>
          <p className="text-muted small">
            {review.templateDescription}
          </p>
        </div>
        <div className="text-end">
          {isCancelled ? (
            <Badge bg="danger" className="fs-6">Cancelled</Badge>
          ) : review.isComplete ? (
            <Badge bg="success" className="fs-6">Complete</Badge>
          ) : isSubmittedForFinalReview ? (
            <Badge bg="info" className="fs-6">Submitted for Final Review</Badge>
          ) : (
            <Badge bg="primary" className="fs-6">
              {completedTasks} / {review.tasks.length} tasks ({progressPercent}%)
            </Badge>
          )}
        </div>
      </div>

      {review.isComplete && review.completedDate && (
        isCancelled ? (
          <Alert variant="danger">
            Review cancelled on {new Date(review.completedDate).toLocaleDateString()}
          </Alert>
        ) : (
          <Alert variant="success">
            ✓ Review completed on {new Date(review.completedDate).toLocaleDateString()}
          </Alert>
        )
      )}

      {isSubmittedForFinalReview && !isFinalReviewer && (
        <Alert variant="info">
          This review has been submitted for final review and is read-only for the original reviewer.
        </Alert>
      )}

      {isFinalReviewer && (
        <Alert variant="warning">
          You are the final reviewer for this review. Complete the steps below to finalise it.
        </Alert>
      )}

      {infoMessage && (
        <Alert variant="success" dismissible onClose={() => setInfoMessage(null)}>
          {infoMessage}
        </Alert>
      )}

      {(review.linkedReports?.length ?? 0) > 0 && (
        <Card className="mb-4 review-detail-card">
          <Card.Header>
            <strong>Linked Reports</strong>
          </Card.Header>
          <Card.Body>
            <ListGroup variant="flush">
              {review.linkedReports?.map((linked) => (
                <ListGroup.Item key={`${linked.m365ReportRunGuid}-${linked.m365ReportId}`} className="px-0">
                  <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                    <div>
                      <div className="fw-semibold">{linked.reportTitle}</div>
                      <div className="small text-muted">
                        Run ID: {linked.m365ReportRunGuid}
                      </div>
                      <div className="small text-muted">
                        {linked.isFromParentReview ? `Inherited from ${linked.sourceReviewName}` : 'Generated for this review'}
                      </div>
                    </div>
                    <div className="text-end">
                      {linked.reportLink ? (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          as="a"
                          href={linked.reportLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open Report
                        </Button>
                      ) : (
                        <Badge bg="secondary">Pending Output</Badge>
                      )}
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-4 review-detail-card">
        <Card.Body>
          <Accordion>
            {review.tasks.map((task, index) => (
              <Accordion.Item eventKey={task.guid} key={task.guid}>
                <Accordion.Header>
                  <div className="d-flex align-items-center w-100">
                    <div className="me-2">
                      {task.isComplete ? (
                        <span className="text-success">✓</span>
                      ) : (
                        <span className="text-muted">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <strong>{task.taskName}</strong>
                      {task.isRequired && <Badge bg="warning" className="ms-2">Required</Badge>}
                      {task.isComplete && (
                        <small className="text-muted ms-2">
                          (Completed {task.completedDate ? new Date(task.completedDate).toLocaleDateString() : ''})
                        </small>
                      )}
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <p className="text-muted">{task.taskDescription}</p>
                  {(() => {
                    const decisionConfig = parseDecisionTaskConfig(task.taskConfig);
                    const reportTriggerConfig = parseReportTriggerTaskConfig(task.taskConfig);
                    const isSelectedTask = selectedTask === task.guid;
                    const canInteract = isTaskInteractable(task, index);
                    const isLocked = !task.isComplete && !isReadOnly && task.isRequired && !canInteract;

                    return (
                      <>

                  {task.comments.length > 0 && (
                    <div className="mb-3">
                      <h6>Comments:</h6>
                      <ListGroup variant="flush">
                        {task.comments.map(comment => (
                          <ListGroup.Item key={comment.guid} className="px-0">
                            <div className="small text-muted">
                              {comment.createdByEmail || 'Unknown'} • {
                                comment.createdDate && new Date(comment.createdDate).getFullYear() > 1 
                                  ? new Date(comment.createdDate).toLocaleString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric', 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })
                                  : 'Unknown date'
                              }
                            </div>
                            <div>{comment.commentText}</div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}

                  {isLocked && (
                    <div className="text-muted small fst-italic mt-1">
                      Complete the previous required step to unlock this step.
                    </div>
                  )}

                  {canInteract && (
                    <div className="mt-3">
                      {reportTriggerConfig && (
                        <Card className="mb-3 border-light-subtle">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div>
                                <div className="fw-semibold">Generate Linked Reports</div>
                                <div className="small text-muted">
                                  Queues report runs and links outputs to this review for audit evidence.
                                </div>
                              </div>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleQueueReports(task.guid, reportTriggerConfig)}
                                disabled={reportSubmittingTaskGuid === task.guid}
                              >
                                {reportSubmittingTaskGuid === task.guid ? 'Queueing...' : 'Generate Reports'}
                              </Button>
                            </div>
                            <div className="small text-muted mt-2">
                              {reportTriggerConfig.allowRegenerate
                                ? 'Re-generation is enabled for this step.'
                                : 'This step is configured for one-time generation.'}
                              {' '}
                              {reportTriggerConfig.includeParentReports
                                ? 'Parent review reports are included when linked.'
                                : 'Only current review reports are shown.'}
                            </div>
                          </Card.Body>
                        </Card>
                      )}

                      {decisionConfig ? (
                        <>
                          <Form.Group>
                            <Form.Label>Decision:</Form.Label>
                            <Form.Select
                              value={isSelectedTask ? decisionOutcome : ''}
                              onChange={(e) => {
                                setDecisionOutcome(e.target.value);
                                setSelectedTask(task.guid);
                              }}
                              disabled={submitting}
                            >
                              <option value="">Select decision...</option>
                              {decisionConfig.options.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>

                          {decisionConfig.noteEnabled && (
                            <Form.Group className="mt-2">
                              <Form.Label>
                                Note{decisionConfig.noteRequired ? ' (required):' : ' (optional):'}
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={isSelectedTask ? commentText : ''}
                                onChange={(e) => {
                                  setCommentText(e.target.value);
                                  setSelectedTask(task.guid);
                                }}
                                placeholder="Enter supporting note..."
                                disabled={submitting}
                              />
                            </Form.Group>
                          )}

                          <div className="mt-2 d-flex justify-content-end gap-2">
                            <Button
                              variant="success"
                              size="sm"
                              className="text-white"
                              onClick={() => handleAddComment(task.guid, true, decisionConfig)}
                              disabled={
                                submitting ||
                                selectedTask !== task.guid ||
                                !(isSelectedTask && decisionOutcome.trim()) ||
                                (decisionConfig.noteRequired && !(isSelectedTask && commentText.trim()))
                              }
                            >
                              {submitting ? 'Saving...' : 'Submit Decision'}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <Form.Group>
                            <Form.Label>Add Comment:</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={isSelectedTask ? commentText : ''}
                              onChange={(e) => {
                                setCommentText(e.target.value);
                                setSelectedTask(task.guid);
                              }}
                              placeholder="Enter your notes, test results, or observations..."
                              disabled={submitting}
                            />
                          </Form.Group>
                          <div className="mt-2 d-flex justify-content-end gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleAddComment(task.guid, false)}
                              disabled={submitting || selectedTask !== task.guid || !commentText.trim()}
                            >
                              {submitting ? 'Saving...' : 'Add Comment Only'}
                            </Button>
                            <Button
                              variant="success"
                              size="sm"
                              className="text-white"
                              onClick={() => handleAddComment(task.guid, true)}
                              disabled={submitting || selectedTask !== task.guid || !commentText.trim()}
                            >
                              {submitting ? 'Saving...' : 'Add Comment & Mark Complete'}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                      </>
                    );
                  })()}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>

      <Button variant="warning" onClick={() => navigate('/reviews')}>
        Back to Reviews
      </Button>
    </Container>
  );
};

export default ReviewDetail;
